import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchUserMilestoneRewards } from "@/api/reading-milestones";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
  milestoneId: number;
};

const PAGE_SIZE = 20;

const MilestoneAchieversTable = ({ milestoneId }: Props) => {
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["milestoneAchievers", milestoneId, page],
    queryFn: () =>
      fetchUserMilestoneRewards({
        milestoneId,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const rewards = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatMinutes = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (rewards.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8 text-sm">
        No users have achieved this milestone yet.
      </p>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">
          {totalCount} user{totalCount !== 1 ? "s" : ""} achieved this
          milestone
        </p>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Achieved At</TableHead>
              <TableHead className="text-right">Total Minutes</TableHead>
              <TableHead className="text-center">Notification</TableHead>
              <TableHead className="text-center">Redeemed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rewards.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage
                        src={r.userAvatarUrl}
                        alt={r.userName}
                      />
                      <AvatarFallback className="text-xs">
                        {r.userName
                          ?.split(" ")
                          .map((w) => w[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2) ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {r.userName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {r.userEmail}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {formatDate(r.achievedAt)}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {formatMinutes(r.totalMinutesAtAchievement)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={
                      r.notificationSent
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-gray-50 text-gray-500 border-gray-200"
                    }
                  >
                    {r.notificationSent ? "Sent" : "Pending"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {r.promotionId ? (
                    <Badge
                      variant="outline"
                      className={
                        r.promotionRedeemed
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }
                    >
                      {r.promotionRedeemed ? "Redeemed" : "Unredeemed"}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneAchieversTable;
